import classnames from 'classnames'
import { Field, FieldProps } from 'formik'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { Color } from 'marketing-site/src/library/utils'
import { NewBrandingHoc } from 'marketing-site/src/library/utils/NewBrandingHoc'
import React, { useState } from 'react'
import circleCheckmark from '../../../images/circleCheckmarkBlack.svg'
import { INumberField } from '../@types/FormField'
import { sanitizeText } from '../Field'
import { IFormFieldProps } from '../Field.interface'
import { Number as NumberLegacy } from './Number-legacy'

export type INumberFieldProps = IFormFieldProps<INumberField>

export const testIds = {
  numberLabel: 'form-field-number-label',
  numberLabelText: 'form-field-number-label-text',
  numberField: (id: string): string => `form-field-number-field-${id}`,
  validationIcon: 'validation-icon',
}

export const NumberDefault = (field: INumberFieldProps): React.ReactElement => {
  const {
    id,
    label,
    prefix,
    required,
    placeholder,
    isDisabled,
    enableValidation,
    hasError,
    onChange,
  } = field
  const fieldId = `${id}_field`
  const labelId = `${id}_label`

  const labelClassNames = classnames('form-field-number', {
    'has-error': enableValidation && hasError,
  })

  const labelText = label && sanitizeText(label)

  const [isValidationSuccessful, setIsValidationSuccessful] = useState<boolean | undefined>(
    undefined,
  )

  const { getTranslatedValidationErrorWithCallback } = useTranslate()

  return (
    <div>
      <label id={labelId} data-testid={testIds.numberLabel} className={labelClassNames}>
        {labelText && (
          <span className="label-text" data-testid={testIds.numberLabelText}>
            {labelText}
          </span>
        )}

        <div className="number-input">
          {prefix}
          <Field
            name={id}
            validate={(value: string) =>
              getTranslatedValidationErrorWithCallback(field, value, setIsValidationSuccessful)
            }
          >
            {({ field: inputProps }: FieldProps) => {
              const customOnChange = (event: React.ChangeEvent<any>) => {
                onChange(event)
                inputProps.onChange(event)
              }

              return (
                <input
                  {...inputProps}
                  id={fieldId}
                  type="text"
                  placeholder={prefix ? ` ${placeholder}` : placeholder}
                  disabled={isDisabled}
                  aria-labelledby={labelId}
                  aria-required={required}
                  data-testid={testIds.numberField(id)}
                  onChange={customOnChange}
                />
              )
            }}
          </Field>

          {isValidationSuccessful && (
            <span className="icon-valid" data-testid={testIds.validationIcon} />
          )}
        </div>
      </label>

      <style jsx>{`
        .form-field-number {
          display: grid;
          grid-row-gap: var(--form-label-spacing-y, 0);

          .number-input {
            width: 100%;
            line-height: 1.5em;
            background-color: ${Color.White};
            border: 2px solid ${Color.Slate};
            border-radius: 6px;
            padding: 10px 17px 9px;
            display: flex;
            flex-direction: row;

            :global(input) {
              flex-grow: 1;
              background-color: ${Color.White};
              color: ${Color.Slate};
              border: none;
              outline: 0;
            }

            &::placeholder {
              color: ${Color.UIGray};
            }
          }

          &.has-error .number-input {
            border: 2px solid ${Color.UIError};
          }
        }

        .icon-valid {
          height: 21px;
          width: 21px;
          background-image: url(${circleCheckmark});
          transform: translate3d(5px, 1px, 0);
        }
      `}</style>
    </div>
  )
}

export const Number = NewBrandingHoc(NumberLegacy, NumberDefault)
